export type DataPrepLayerGroup = "inputs" | "underground" | "aerial" | "leadins";

type NetworkLayerGroup = Extract<DataPrepLayerGroup, "underground" | "aerial">;

export interface LayerGroupStatus {
  /**
   * At least one of the group's layers is present
   */
  isPresent: boolean;
  /**
   * Every layer in the group is present
   */
  isComplete: boolean;
  /**
   * Names of the group's layers that aren't present
   */
  missingLayerNames: string[];
}

export interface DataPrepStatus {
  groups: Record<DataPrepLayerGroup, LayerGroupStatus>;
  /**
   * Every input layer that data prep needs is present
   */
  hasRequiredInputLayers: boolean;
  /**
   * A complete candidate network is present
   */
  hasCandidateNetwork: boolean;
  /**
   * Leadins and a complete network are present
   */
  isDataPrepComplete: boolean;
}

export interface GroupLayer {
  id: string;
  name: string;
}

const getLayerGroupStatus = (layerKeys: string[], layers: GroupLayer[]): LayerGroupStatus => {
  const missingLayerNames = layers.filter(({ id }) => !layerKeys.includes(id)).map(({ name }) => name);
  return {
    isPresent: missingLayerNames.length < layers.length,
    isComplete: missingLayerNames.length === 0,
    missingLayerNames,
  };
};

/**
 * The current status of the layer groups relevant to data prep.
 *
 * @param candidateNetworkGroups the groups that can form a candidate network
 */
export const getDataPrepStatus = (
  layerKeys: string[],
  layerGroups: Record<DataPrepLayerGroup, GroupLayer[]>,
  candidateNetworkGroups: NetworkLayerGroup[]
): DataPrepStatus => {
  const groups = {
    inputs: getLayerGroupStatus(layerKeys, layerGroups.inputs),
    underground: getLayerGroupStatus(layerKeys, layerGroups.underground),
    aerial: getLayerGroupStatus(layerKeys, layerGroups.aerial),
    leadins: getLayerGroupStatus(layerKeys, layerGroups.leadins),
  };
  const hasCandidateNetwork = candidateNetworkGroups.some((group) => groups[group].isComplete);

  return {
    groups,
    hasRequiredInputLayers: groups.inputs.isComplete,
    hasCandidateNetwork,
    isDataPrepComplete: groups.leadins.isComplete && hasCandidateNetwork,
  };
};
